import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { emptyArray } from 'ui-components/default-props/empty';
import { PagingContext } from '../../../contexts/paging-context';
import { notContainsIndicator } from '../../../services/filtering/collection-filter-mapping-service';
import { supplierApi } from '../purchase-orders/api/supplier-api';
import { supplierGridProperties } from './data/suppliers-properties';
import { messages } from './suppliers-grid-context.messages';
import type { AppliedFilter, OrderByColumn, SortDirection, Supplier } from './types/supplier-types';

type SortingRequest = {
  orderBy?: string;
  direction?: SortDirection;
};

type CollectionFilter = {
  column: string;
  isIn: boolean;
  values: string[];
};

type FilterRequest = {
  filter?: {
    collectionFilters: CollectionFilter[];
  };
};

const columnIdToApiColumnMap: Record<string, string> = {
  [supplierGridProperties.supplierName]: 'SupplierName',
  [supplierGridProperties.supplierEmail]: 'SupplierEmail',
  [supplierGridProperties.contactName]: 'ContactName',
  [supplierGridProperties.supplierCountry]: 'SupplierCountry',
};

const transformSortingToApiRequest = (orderByColumn: OrderByColumn | null): SortingRequest => {
  if (!orderByColumn || !orderByColumn.id || !orderByColumn.sorted) {
    return {};
  }

  const apiColumnName = columnIdToApiColumnMap[orderByColumn.id];
  if (!apiColumnName) {
    return {};
  }

  return {
    orderBy: apiColumnName,
    direction: orderByColumn.sorted === 'ascending' ? 'ascending' : 'descending',
  };
};

const transformFiltersToApiRequest = (appliedFilters: AppliedFilter[]): FilterRequest => {
  if (!appliedFilters || appliedFilters.length === 0) {
    return {};
  }

  type GroupedFilter = {
    column: string;
    operator: string;
    values: string[];
  };

  const groupedFilters = appliedFilters.reduce<Record<string, GroupedFilter>>((acc, filter) => {
    const { filterById, criterionArgumentValue, criterionOperatorValue } = filter;
    const groupKey = `${filterById}:${criterionOperatorValue}`;

    const currentValues = acc[groupKey]?.values ?? [];
    const newValues = Array.isArray(criterionArgumentValue)
      ? [...currentValues, ...criterionArgumentValue]
      : [...currentValues, criterionArgumentValue];

    return {
      ...acc,
      [groupKey]: {
        column: filterById,
        operator: criterionOperatorValue,
        values: newValues,
      },
    };
  }, {});

  const collectionFilters = Object.values(groupedFilters).map(({ column, operator, values }: GroupedFilter) => {
    const isIn = operator !== notContainsIndicator;

    return {
      column,
      isIn,
      values,
    };
  });

  return {
    filter: {
      collectionFilters,
    },
  };
};

type SuppliersGridContextValue = {
  appliedFilters: AppliedFilter[];
  onFiltersChanged: (filters: AppliedFilter[]) => void;
  clearFilters: () => void;
  orderByColumn: OrderByColumn | null;
  onOrderByChanged: (columns: OrderByColumn[]) => void;
  gridData: Supplier[];
  isLoading: boolean;
  totalRecords: number;
  refreshSuppliers: () => Promise<void>;
  getSupplierById: (supplierId: string) => Promise<Supplier | null>;
};

const SuppliersGridContext = createContext<SuppliersGridContextValue | null>(null);

export const useSuppliersGridContext = (): SuppliersGridContextValue => {
  const context = useContext(SuppliersGridContext);
  if (!context) {
    throw new Error('useSuppliersGridContext must be used within SuppliersGridContextProvider');
  }
  return context;
};

type SuppliersGridContextProviderProps = {
  children: ReactNode;
};

export const SuppliersGridContextProvider = ({ children }: SuppliersGridContextProviderProps) => {
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(emptyArray as AppliedFilter[]);
  const [orderByColumn, setOrderByColumn] = useState<OrderByColumn | null>(null);
  const [gridData, setGridData] = useState<Supplier[]>(emptyArray as Supplier[]);
  const [isLoading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const currentRequestRef = useRef<{ cancel: () => void } | null>(null);
  const pagingContext = useContext(PagingContext);

  if (!pagingContext) {
    throw new Error('SuppliersGridContextProvider must be used within PagingContextProvider');
  }

  const { page, pageSize, updatePageInformation } = pagingContext;

  const fetchSuppliers = useCallback(async () => {
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }

    setLoading(true);

    try {
      const filterRequest = transformFiltersToApiRequest(appliedFilters);
      const sortingRequest = transformSortingToApiRequest(orderByColumn);
      const request = supplierApi.getAllSuppliersGrid({
        ...filterRequest,
        ...sortingRequest,
        page: {
          pageNumber: page,
          pageSize,
        },
      });

      currentRequestRef.current = request;
      const response = await request.promise;

      const suppliers = (response.suppliers ?? []) as Supplier[];
      const suppliersWithId = suppliers.map((supplier) => ({
        ...supplier,
        id: String(supplier.supplierId),
      }));
      setGridData(suppliersWithId);
      setTotalRecords(response.totalCount ?? 0);
    } catch {
      setGridData([]);
      setTotalRecords(0);
      NotificationService.showNotification({
        title: <FormattedMessage {...messages.fetchSuppliersErrorTitle} />,
        content: <FormattedMessage {...messages.fetchSuppliersErrorContent} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
      });
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, orderByColumn, page, pageSize]);

  const refreshSuppliers = useCallback(() => {
    return fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    fetchSuppliers();

    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel();
      }
    };
  }, [fetchSuppliers]);

  const onFiltersChanged = useCallback(
    (filters: AppliedFilter[]) => {
      setAppliedFilters(filters);
      updatePageInformation(1, pageSize);
    },
    [pageSize, updatePageInformation],
  );

  const clearFilters = useCallback(() => {
    setAppliedFilters(emptyArray as AppliedFilter[]);
    updatePageInformation(1, pageSize);
  }, [pageSize, updatePageInformation]);

  const onOrderByChanged = useCallback(
    (columns: OrderByColumn[]) => {
      const sortedColumn = columns.find((col) => col.sorted === 'ascending' || col.sorted === 'descending');
      setOrderByColumn(sortedColumn ?? null);
      updatePageInformation(1, pageSize);
    },
    [pageSize, updatePageInformation],
  );

  const getSupplierById = useCallback(async (supplierId: string): Promise<Supplier | null> => {
    try {
      return await supplierApi.getSupplier(supplierId);
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(
    () => ({
      appliedFilters,
      onFiltersChanged,
      clearFilters,
      orderByColumn,
      onOrderByChanged,
      gridData,
      isLoading,
      totalRecords,
      refreshSuppliers,
      getSupplierById,
    }),
    [
      appliedFilters,
      clearFilters,
      getSupplierById,
      gridData,
      isLoading,
      onFiltersChanged,
      onOrderByChanged,
      orderByColumn,
      refreshSuppliers,
      totalRecords,
    ],
  );

  return <SuppliersGridContext.Provider value={value}>{children}</SuppliersGridContext.Provider>;
};
